import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { CandidateSuccessModal, FormDialog } from '@components';
import { CandidateRejectionReasons } from './components/CandidateRejectionReasons';
import {
  ScheduleInterviewFields,
  ScheduleInterviewNote,
} from './components/ScheduleInterviewFields';
import { ResolutionBlock } from './components/ResolutionBlock';
import { Stack, TextField } from '@mui/material';

import { getUserOffset } from '@utils';
import { validationSchema } from './validationSchema';

import { CandidateFeedbackFormValues, ICandidateDetails } from '@types';
import { CandidateDetailsAction, ClientFeedbackResolution } from '@constants';

const RESOLUTION_BY_ACTION: Partial<
  Record<CandidateDetailsAction, ClientFeedbackResolution>
> = {
  [CandidateDetailsAction.APPROVE_CANDIDATE]:
    ClientFeedbackResolution.HiringApproved,
  [CandidateDetailsAction.CLIENT_INTERVIEW_PASSED]:
    ClientFeedbackResolution.HiringApproved,
  [CandidateDetailsAction.SCHEDULE_INTERVIEW]:
    ClientFeedbackResolution.CallRequested,
  [CandidateDetailsAction.SCHEDULE_NEXT_ROUND]:
    ClientFeedbackResolution.CallRequested,
  [CandidateDetailsAction.SKIP_CANDIDATE]: ClientFeedbackResolution.Rejected,
  [CandidateDetailsAction.CLIENT_INTERVIEW_FAILED]:
    ClientFeedbackResolution.Rejected,
};

interface IFeedbackSubject {
  name: string;
  subtitle: string;
  details: ICandidateDetails | null;
  clientTimezone?: string | null;
}

interface IClientFeedbackDialogProps {
  subject: IFeedbackSubject | null;
  action: CandidateDetailsAction | null;
  isLoading: boolean;
  error: string | null;
  interviewRequested: boolean;
  onClose: () => void;
  onSubmit: (values: CandidateFeedbackFormValues) => Promise<boolean>;
}

export const ClientFeedbackDialog: React.FC<IClientFeedbackDialogProps> = ({
  subject,
  action,
  isLoading,
  error,
  interviewRequested,
  onClose,
  onSubmit,
}) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false);

  const isRejection = [
    CandidateDetailsAction.SKIP_CANDIDATE,
    CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
  ].includes(action!);
  const isNextRound = action === CandidateDetailsAction.SCHEDULE_NEXT_ROUND;
  const isScheduling =
    action === CandidateDetailsAction.SCHEDULE_INTERVIEW || isNextRound;
  const candidateName = subject?.name ?? '';
  const header = {
    label: isNextRound
      ? 'Schedule next round'
      : isScheduling
        ? 'Schedule interview'
        : candidateName,
    subLabel: isScheduling || !subject ? candidateName : subject.subtitle,
  };
  const [successHeader, setSuccessHeader] = useState(header);

  const formik = useFormik<CandidateFeedbackFormValues>({
    initialValues: {
      resolution: null,
      rejectionReasons: null,
      anotherRoundReason: null,
      interviewSlots: null,
      interviewOption: null,
      clientCalendar: null,
      details: null,
      clientTimezone: subject?.clientTimezone || getUserOffset(),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (await onSubmit(values)) {
        onClose();
        setSuccessHeader(header);
        setSuccessModalOpen(true);
      }
    },
  });

  useEffect(() => {
    if (action) {
      if (
        [
          CandidateDetailsAction.APPROVE_CANDIDATE,
          CandidateDetailsAction.SCHEDULE_INTERVIEW,
          CandidateDetailsAction.SCHEDULE_NEXT_ROUND,
          CandidateDetailsAction.SKIP_CANDIDATE,
          CandidateDetailsAction.CLIENT_INTERVIEW_PASSED,
          CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
        ].includes(action)
      ) {
        formik.setFieldValue(
          'resolution',
          RESOLUTION_BY_ACTION[action] ?? null,
        );
        setModalOpen(true);
      }
    } else {
      formik.resetForm();
      setModalOpen(false);
    }
  }, [action]);

  const detailsError =
    ((formik.submitCount || formik.touched.details) && formik.errors.details) ||
    '';

  const missing = isScheduling
    ? isNextRound && !formik.values.anotherRoundReason
      ? 'Say what this round should settle to submit.'
      : !formik.values.interviewSlots?.some((slot) => slot.start && slot.end) &&
          !formik.values.clientCalendar
        ? 'Pick a time or share your calendar to submit.'
        : ''
    : isRejection && !formik.values.rejectionReasons?.length
      ? 'Pick a reason to submit.'
      : isRejection && !formik.values.details?.trim()
        ? 'Say what the candidate was missing to submit.'
        : '';

  if (!subject) return null;

  return (
    <>
      <FormDialog
        isOpen={isModalOpen}
        onClose={onClose}
        {...header}
        onSubmit={formik.handleSubmit}
        isLoading={isLoading}
        submitDisabled={Boolean(missing)}
        submitBtnLabel={isScheduling ? 'Request this slot' : undefined}
        hint={missing}
        error={error}
      >
        <Stack gap="1.5rem">
          <ResolutionBlock candidateDetailsAction={action} />
          {isRejection && <CandidateRejectionReasons formik={formik} />}
          {isScheduling && (
            <ScheduleInterviewFields
              candidateDetails={subject.details}
              formik={formik}
              isNextRound={isNextRound}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            required={isRejection}
            name="details"
            label={
              isNextRound
                ? 'Your feedback on the last round'
                : isScheduling
                  ? 'Anything else'
                  : 'Notes'
            }
            placeholder={
              isRejection
                ? 'What was the candidate missing? This is what improves the next submission.'
                : isNextRound
                  ? 'How did the round go, and what is still open?'
                  : isScheduling
                    ? 'Who should attend, what you are waiting on, anything the coordinator needs'
                    : 'Please add any suggestions you have about this candidate or the interview.'
            }
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            value={formik.values.details || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={detailsError || undefined}
            error={!!detailsError}
          />
          {isScheduling && <ScheduleInterviewNote />}
        </Stack>
      </FormDialog>
      <CandidateSuccessModal
        header={successHeader}
        isOpen={isSuccessModalOpen}
        isPassed={interviewRequested}
        label={
          interviewRequested
            ? "Thanks, we'll set up an interview"
            : 'Thank you for your response!'
        }
        subLabel={'Our Recruiter may get back to you to clarify details.'}
        handleClose={() => setSuccessModalOpen(false)}
      />
    </>
  );
};
